import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import "react-quill/dist/quill.snow.css";
import defaultTemplateImage from "../../assets/images/template-default.png";
import EventService from "../../services/EventService.js";
import PaymentService from "../../services/PaymentService.js";
import UserSessionManager from "../../utils/UserSessionManager.js";
import "../../assets/css/AddDetailsTab.css";
import ReusableCheckbox from "../../components/common/ReusableCheckbox";
import PrimaryButton from "../../components/common/PrimaryButton.js";
import { loadRazorpay, cleanupRazorpay } from "../../utils/razorpay.js";
import Swal from "sweetalert2";

const BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL;

const EventSharing = () => {
  const { eventId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [campaignName, setCampaignName] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [templateHtml, setTemplateHtml] = useState("");
  const [backgroundImage, setBackgroundImage] = useState(defaultTemplateImage);
  const [formFields, setFormFields] = useState([]);
  const [eventPriceTagLine, setEventPriceTagLine] = useState("");
  const [eventPrice, setEventPrice] = useState("");
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [agreeToEventUpdates, setAgreeToEventUpdates] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [razorpayKeyId, setRazorpayKeyId] = useState("");
  const [status, setStatus] = useState("");
  const [guestPaymentSuccess, setGuestPaymentSuccess] = useState(false);

  const templateRef = useRef(null);
  const getAuthContext = () => {
    const userSession = UserSessionManager.getSession();
    const isLoggedIn = !!userSession;
    const businessAdminId = new URLSearchParams(window.location.search).get("businessAdminId");
    return { isLoggedIn, businessAdminId };
  };

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const { isLoggedIn, businessAdminId } = getAuthContext();

        const resp = isLoggedIn
          ? await EventService.fetchDraftEventById(eventId)
          : await EventService.fetchEventForRegistration(eventId, businessAdminId);

        if (resp.data?.success) {
          console.log("Event data:", resp.data.data);
          const ev = resp.data.data;
          setCampaignName(ev.eventName || "");
          setDescription(ev.eventDetails || "");
          setStartDate(ev.startDate ? ev.startDate.substring(0, 10) : "");
          setEndDate(ev.endDate ? ev.endDate.substring(0, 10) : "");
          setTemplateHtml(ev.eventTemplate?.templateBody || "");
          setEventPriceTagLine(ev.eventPriceTagline || "");
          setEventPrice(ev.eventEnterPrice || "0");
          setRazorpayKeyId(ev.razorPayInformations?.razorpayKeyId || "");

          const imagePath = ev.eventTemplate?.templateImage;
          setBackgroundImage(
            imagePath ? `${imagePath}` : defaultTemplateImage
          );

          const inputs = (ev.eventInputs || []).map((inp) => ({
            label: inp.inputTitle,
            required: inp.isRequire,
          }));
          setFormFields(inputs);
        } else {
          setError("Event not found");
        }
      } catch (err) {
        console.error("Fetch error:", err);
        setError("Error loading event");
      } finally {
        setLoading(false);
      }
    };

    if (eventId) fetchEvent();
    else {
      setError("No event ID provided");
      setLoading(false);
    }
  }, [eventId]);

  // === Resolve Image and Size ===
  useEffect(() => {
    const img = new Image();
    img.src = backgroundImage || defaultTemplateImage;
    img.onload = () =>
      setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => setImageSize({ width: 900, height: 400 });

  }, [backgroundImage]);


  if (loading) return <div>Loading...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (guestPaymentSuccess) {
    return (
      <div className="event-campaign-container preview-wrapper">
        <div
          style={{
            minHeight: "50vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            color: "green",
            fontSize: "42px",
            fontWeight: 700,
          }}
        >
          Thank you for registration!
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreeToEventUpdates) return;

    const paymentRequest = mapFormValuesToPaymentRequest();
    const { isLoggedIn, businessAdminId } = getAuthContext();
    try {
      console.log("🔹 Payment Request:", paymentRequest);
      const res = isLoggedIn
        ? await PaymentService.createOrder(paymentRequest, agreeToEventUpdates)
        : await PaymentService.createOrderForRegistration(paymentRequest, agreeToEventUpdates, businessAdminId);

      if (res.data.success) {
        const order = res.data.data;

        openRazorpayCheckout(order, { isLoggedIn, businessAdminId });
      }
    } catch (err) {
      console.error("Payment order error:", err);
      alert(err.response?.data?.message || "Error initiating payment");
    }
  };

  const openRazorpayCheckout = async (order, { isLoggedIn, businessAdminId }) => {
    const loaded = await loadRazorpay();
    if (!loaded || !window.Razorpay) {
      const SWEET_ALERT_CONFIRM_COLOR = 'rgb(208 125 63)';
      await Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Failed to load Razorpay SDK. Please check your internet connection.',
        confirmButtonColor: SWEET_ALERT_CONFIRM_COLOR,
      });
      return;
    }

    const options = {
      key: razorpayKeyId,
      amount: order.amount,
      currency: order.currency,
      order_id: order.razorpayOrderId,
      name: campaignName,
      description: "Event Registration",

      prefill: {
        name: formValues["First Name"],
        email: formValues["Email"],
        contact: formValues["WhatsApp Number"]
      },
      handler: async function (response) {
        console.log("🔹 Razorpay Handler Response:", response);
        cleanupRazorpay();
        setStatus("Verifying payment...");

        // 3️⃣ Step 3: Verify Payment via backend
        try {
          const verifyPayload = {
            razorpayOrderId: response.razorpay_order_id,
            razorpayPaymentId: response.razorpay_payment_id,
            razorpaySignature: response.razorpay_signature,
            eventId: eventId,
          };
          const verifyRes = isLoggedIn
            ? await PaymentService.verifyPayment(verifyPayload)
            : await PaymentService.verifyPaymentForRegistration(verifyPayload, businessAdminId);

          const msg = verifyRes.data.data.isSuccess
            ? "Payment successful and registration completed!"
            : "Payment Verification Failed!";

          if (verifyRes.data.data.isSuccess && !isLoggedIn) {
            setGuestPaymentSuccess(true);
            setStatus("Thank you for registration!");
          }

          setStatus(msg);
          alert(msg);
        } catch (err) {
          console.error(err);
          const errorMsg = "Verification failed. Check logs.";
          setStatus(errorMsg);
          alert(errorMsg);
        }
      },

      theme: {
        color: "#3399cc"
      },
      modal: {
        ondismiss: function () {
          cleanupRazorpay();
        }
      }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };


  const getFieldValue = (keywords = []) => {
    return (
      Object.entries(formValues).find(([key]) =>
        keywords.some(keyword =>
          key.toLowerCase().includes(keyword.toLowerCase())
        )
      )?.[1] || ""
    );
  };

  const mapFormValuesToPaymentRequest = () => {
    return {
      firstName: getFieldValue(["first", "name"]),
      lastName: getFieldValue(["last"]),
      email: getFieldValue(["email", "mail"]),
      whatsAppNumber: getFieldValue(["whatsapp", "mobile", "phone", "contact"]),
      companyName: getFieldValue(["company", "organization"]),
      amount: parseFloat(eventPrice || 0),
      currency: "INR",
      paymentMethod: "netbanking",
      eventId: eventId,
    };
  };



  return (
    <div className="event-campaign-container preview-wrapper">
      {/* === Banner === */}
      <div
        className="event-banner"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          width: imageSize.width ? `${imageSize.width}px` : "100%",
          height: imageSize.height ? `${imageSize.height}px` : "400px",
        }}
      >
        <div
          className="event-template"
          ref={templateRef}
          dangerouslySetInnerHTML={{ __html: templateHtml }}
        ></div>
      </div>

      {/* === Description & Price === */}
      <div className="event-description aligned-section">
        <div
          className="event-desc-text"
          dangerouslySetInnerHTML={{ __html: description }}
        ></div>

        {eventPriceTagLine && (
          <div className="event-price-tag">
            <p>
              {eventPriceTagLine}
              <span style={{ color: "red" }}> ₹ {eventPrice}</span>
            </p>
          </div>
        )}
      </div>

      {/* === Registration Form === */}
      <form className="register-section aligned-section" onSubmit={handleSubmit}>
        <h4>Register</h4>
        <div className="form-fields">
          {formFields.length === 0 && (
            <p className="text-muted">No fields added.</p>
          )}
          {formFields.map((field, idx) => (
            <div className="form-group" key={idx}>
              <label>
                {field.label}
                {field.required && <span className="required-star">*</span>}
              </label>
              <input
                type="text"
                placeholder={field.label}
                className="input-field"
                required={field.required}
                value={formValues[field.label] || ""}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    [field.label]: e.target.value,
                  }))
                }
              />
            </div>
          ))}
        </div>

        {/* === Payment Section === */}
        <ReusableCheckbox
          id="agreeToEventSharing"
          name="agreeToEventSharing"
          label="I agree to receive information about the event over WhatsApp and emails, which will be sent by ProspectPulse. 
            I understand that the messages will/may include updates and follow-ups for current and future events."
          checked={agreeToEventUpdates}
          onChange={(e) => {
            setAgreeToEventUpdates(e.target.checked);
          }}
        />

        <br />
        <div className="d-flex justify-content-end">
          <PrimaryButton type="submit"
            disabled={!agreeToEventUpdates}
            className={!agreeToEventUpdates ? "opacity-50 cursor-not-allowed" : ""}
          >
            Register
          </PrimaryButton>
        </div>
      </form>
      {status && !guestPaymentSuccess && (
        <div className="aligned-section" style={{ marginTop: "10px" }}>
          {status}
        </div>
      )}
    </div>
  );
};

export default EventSharing;
